import React from 'react';
import { Select as AntSelect } from 'antd';
import styled from 'styled-components';
import { Colors } from '../../constants';
import { Text } from '../Text';
import { Box } from '../Box';
import { IcTick, IcChevronDown } from 'basic-ui-lib/icons';

interface Option {
  value: string;
  label: string;
}

interface SelectWithLabelProps {
  value?: string;
  options: Option[];
  onChange?: (value: string, option?: Option) => void;
  allowClear?: boolean;
  placeholder?: string;
  label?: string;
  style?: React.CSSProperties;
  className?: string;
  width?: number | string;
}

const renderOption = (option: any, currentValue?: string) => {
  return (
    <SelectItem>
      <Text body_1 regular>
        {option.label}
      </Text>
      <Box display={currentValue === option.value ? 'block' : 'none'}>
        <IcTick fill={Colors.primary[5]} width={16} height={16} />
      </Box>
    </SelectItem>
  );
};

const SelectWithLabel: React.FC<SelectWithLabelProps> = ({
  value,
  options,
  onChange,
  allowClear = false,
  placeholder = 'Please select',
  label,
  style,
  className,
  width,
}) => {
  return (
    <Container className={className} style={{ width, ...style }}>
      {label && <LabelText>{label}</LabelText>}
      <StyledSelect
        value={value || undefined}
        onChange={(val, option) => onChange?.(val as string, option as Option)}
        allowClear={allowClear}
        placeholder={placeholder}
        $hasLabel={!!label}
        popupMatchSelectWidth={true}
        suffixIcon={<IcChevronDown fill={Colors.primary[5]} width={16} height={16} />}
        options={options}
        optionRender={(option) => renderOption(option, value)}
        filterOption={(input, option) =>
          (option?.label as string).toLowerCase().includes(input.toLowerCase())
        }
        showSearch
      />
    </Container>
  );
};

export default SelectWithLabel;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LabelText = styled.span`
  position: absolute;
  top: 6px;
  left: 12px;
  font-family: 'Poppins';
  font-size: 11px;
  font-weight: 400;
  color: ${Colors.neutral.text[4]};
  z-index: 1;
  pointer-events: none;
`;

const StyledSelect = styled(AntSelect)<{ $hasLabel?: boolean }>`
  width: 100%;
  height: 49px;

  .ant-select-selector {
    width: 100%;
    height: 100% !important;
    padding: ${({ $hasLabel }) => ($hasLabel ? '22px 12px 6px 12px' : '12px')} !important;
    border: 1px solid ${Colors.neutral.border[1]} !important;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s ease;
    background: ${Colors.neutral.bg[1]};

    &:hover {
      border-color: ${Colors.primary[5]} !important;
    }
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${Colors.primary[5]} !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item {
    font-family: 'Poppins';
    font-weight: 500;
    font-size: 14px;
    text-align: left;
    color: ${Colors.neutral.text[5]} !important;
    line-height: 20px !important;
    padding-inline-end: 24px;
  }

  .ant-select-selection-placeholder {
    font-family: 'Poppins';
    font-weight: 500;
    font-size: 14px;
    text-align: left;
    color: ${Colors.neutral.text[4]} !important;
  }

  .ant-select-arrow {
    color: ${Colors.primary[5]};
    right: 12px;
  }
`;

const SelectItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
`;
